// Pattern: docs/design-patterns/frontend/accessibility.md (focus-trapped dialog)
// Pattern: docs/design-patterns/frontend/ui-component-states.md (modal-focus-trap, modal-dismissal, modal-focus-return)
//
// <lg slide-over rendering SidebarNav (always expanded) for mobile nav.
// Built on HeroUI's Drawer — react-aria under the hood already provides
// focus-trapping, Escape-to-close, overlay-click-to-close, body-scroll-lock,
// and focus-return-to-trigger, so this component doesn't hand-roll any of
// that (see B6.6: "prefer HeroUI Drawer if present in the installed HeroUI
// version" — it is, v2.8.6).
//
// disableAnimation: verified in Storybook that this HeroUI/framer-motion
// combination can settle the enter transition's `x` transform at a
// non-zero, viewport-dependent value instead of 0, leaving the drawer's
// content rendered but positioned off-screen (confirmed via computed
// styles, not just a slow transition — waiting it out doesn't resolve it).
// Content and focus/keyboard behavior are unaffected either way; disabling
// the slide animation trades a cosmetic transition for guaranteed correct,
// immediate positioning — worth it for a navigation-critical control that
// Playwright (TEST-001) also needs to interact with deterministically.
import type { JSX } from 'react';
import { Drawer, DrawerBody, DrawerContent, DrawerHeader } from '@heroui/react';

import { getNavSections } from './navConfig';
import { SidebarNav } from './SidebarNav';

export interface MobileNavDrawerProps {
  /** Whether the drawer is open. */
  open: boolean;
  /** Invoked when the drawer should close (Escape, overlay click, close button, or nav-item click). */
  onClose: () => void;
}

export function MobileNavDrawer({ onClose, open }: MobileNavDrawerProps): JSX.Element {
  return (
    <Drawer isOpen={open} onClose={onClose} placement="left" size="xs" disableAnimation>
      <DrawerContent>
        <DrawerHeader className="border-b border-default">Navigation</DrawerHeader>
        <DrawerBody className="px-0 py-2">
          <SidebarNav sections={getNavSections()} collapsed={false} onNavigate={onClose} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
